import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { Menu, MenuContent, MenuItem, MenuLabel, NamedLink } from '../../../components';

import css from './ListingActionsMenu.module.css';

/**
 * Everything a seller can do to one advertisement, in one place.
 *
 * The card used to spread its actions across four controls — two status dropdowns, a
 * pair of footer buttons, and the subscription value, which was a button styled as
 * plain coloured text and so was not read as clickable at all. That last one is why
 * the subscription panel was effectively undiscoverable.
 *
 * Items are described by `listingCardMenu.js` and rendered blindly here: this
 * component decides nothing about which actions apply.
 *
 * @param {Object} props
 * @param {Array} props.items descriptors from deriveMenuItems()
 * @param {Function} props.onSelect called with the item key
 * @param {Object} [props.linkPropsByKey] items that navigate rather than act
 * @param {boolean} props.isOpen
 * @param {Function} props.onToggleOpen
 */
const ListingActionsMenu = props => {
  const { items, onSelect, linkPropsByKey = {}, isOpen, onToggleOpen, className } = props;
  const intl = useIntl();

  if (!items?.length) {
    return null;
  }

  const renderItemBody = item => (
    <>
      <span
        className={classNames(css.itemLabel, { [css.itemLabelDestructive]: item.isDestructive })}
      >
        <FormattedMessage id={item.labelId} />
      </span>
      {/* The subtitle carries the billing consequence. That is the whole point of it:
          nobody should have to deduce that marking an advertisement sold is the way
          to stop paying. */}
      <span className={css.itemSubtitle}>
        <FormattedMessage id={item.subtitleId} />
      </span>
    </>
  );

  return (
    <div className={classNames(css.root, className)}>
      <Menu isOpen={isOpen} onToggleActive={onToggleOpen} contentPlacementOffset={4}>
        <MenuLabel
          rootClassName={classNames(css.trigger, { [css.triggerOpen]: isOpen })}
          aria-label={intl.formatMessage({ id: 'ListingActionsMenu.ariaLabel' })}
        >
          <span className={css.triggerGlyph} aria-hidden="true">
            •••
          </span>
        </MenuLabel>
        {/* `className`, not `rootClassName`: MenuContent treats rootClassName as a
            replacement, and its own root is what carries `position: absolute` and the
            popup z-index. Overriding it drops the dropdown into normal flow, which
            stretches the whole menu and shoves the trigger out of place. */}
        <MenuContent className={css.content}>
          {items.map(item => {
            const linkProps = linkPropsByKey[item.key];

            return (
              <MenuItem
                key={item.key}
                rootClassName={classNames(css.item, {
                  [css.itemStartsMoneyGroup]: item.startsMoneyGroup,
                })}
              >
                {linkProps ? (
                  <NamedLink
                    className={css.itemButton}
                    name={linkProps.name}
                    params={linkProps.params}
                    onClick={e => e.stopPropagation()}
                  >
                    {renderItemBody(item)}
                  </NamedLink>
                ) : (
                  <button
                    type="button"
                    className={css.itemButton}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelect(item.key);
                    }}
                  >
                    {renderItemBody(item)}
                  </button>
                )}
              </MenuItem>
            );
          })}
        </MenuContent>
      </Menu>
    </div>
  );
};

export default ListingActionsMenu;
